import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '@sb-shared/shared.module';
import {
  VectareConfiguration,
  VectareConfigurationUpdateRequest
} from '../../models/vectare-configuration.model';
import { VectareTransportService } from '../../services/vectare-transport.service';

/**
 * System Admin > Main Settings > Vectare Transport.
 *
 * Standalone so it can be bridged into the AngularJS main-settings container with
 * downgradeComponent - a component declared in the lazy SysadminModule would not be available when
 * the downgraded directive is registered at AngularJS bootstrap.
 */
@Component({
  selector: 'sb-vectare-transport',
  templateUrl: './vectare-transport.component.html',
  styleUrls: ['./vectare-transport.component.scss'],
  imports: [SharedModule, FormsModule]
})
export class VectareTransportComponent implements OnChanges {
  @Input() organisationId: number;

  isLoading = false;
  isSaving = false;
  isSyncing = false;
  syncRequested = false;
  errorMessage = '';
  errorMessageParameters: unknown = null;

  // The last loaded configuration, kept so Cancel can restore it and so we can tell whether the
  // form is dirty and whether Sync now should be available.
  saved: VectareConfiguration = null;

  isEnabled = false;
  vectareUrl = '';
  owningPersonId = 0;

  constructor(private vectareTransportService: VectareTransportService) {}

  get owningPersonOptions() {
    return this.saved?.owningPersonOptions ?? [];
  }

  get history() {
    return this.saved?.history ?? [];
  }

  get owningPersonName(): string {
    return this.owningPersonOptions.find(option => option.personId === this.owningPersonId)?.name ?? '';
  }

  get isUrlValid(): boolean {
    return !!this.vectareUrl && this.vectareUrl.startsWith('https://') && this.vectareUrl.endsWith('/');
  }

  get isValid(): boolean {
    // A disabled integration keeps its configuration, so the fields only become mandatory when
    // Support is enabling it.
    return !this.isEnabled || (this.isUrlValid && this.owningPersonId > 0);
  }

  get isDirty(): boolean {
    if (!this.saved) {
      return false;
    }

    return this.isEnabled !== this.saved.isEnabled
      || this.vectareUrl !== (this.saved.vectareUrl ?? '')
      || this.owningPersonId !== this.saved.owningPersonId;
  }

  get canSave(): boolean {
    return this.isDirty && this.isValid && !this.isSaving;
  }

  // Only offer a manual sync once an enabled configuration has actually been saved, and stop
  // offering it after a request so nobody queues the same import twice.
  get canSync(): boolean {
    return !!this.saved?.isEnabled && !this.isDirty && !this.isSyncing && !this.syncRequested;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organisationId'] && this.organisationId) {
      this.load();
    }
  }

  load(): void {
    this.isLoading = true;
    this.clearMessages();

    this.vectareTransportService.getConfiguration(this.organisationId).subscribe({
      next: configuration => {
        this.applyConfiguration(configuration);
        this.isLoading = false;
      },
      error: () => {
        this.saved = null;
        this.isLoading = false;
      }
    });
  }

  cancel(): void {
    this.clearMessages();

    if (this.saved) {
      this.applyConfiguration(this.saved);
    }
  }

  save(): void {
    if (!this.canSave) {
      return;
    }

    const request: VectareConfigurationUpdateRequest = {
      isEnabled: this.isEnabled,
      vectareUrl: this.vectareUrl,
      owningPersonId: this.owningPersonId
    };

    this.isSaving = true;
    this.clearMessages();

    this.vectareTransportService.saveConfiguration(this.organisationId, request).subscribe({
      next: configuration => {
        this.applyConfiguration(configuration);
        this.isSaving = false;
      },
      error: error => {
        this.setError(error);
        this.isSaving = false;
      }
    });
  }

  syncNow(): void {
    if (!this.canSync) {
      return;
    }

    this.isSyncing = true;
    this.clearMessages();

    this.vectareTransportService.requestSync(this.organisationId).subscribe({
      next: () => {
        this.syncRequested = true;
        this.isSyncing = false;
      },
      error: error => {
        this.setError(error);
        this.isSyncing = false;
      }
    });
  }

  private applyConfiguration(configuration: VectareConfiguration): void {
    this.saved = configuration;
    this.isEnabled = configuration?.isEnabled ?? false;
    this.vectareUrl = configuration?.vectareUrl ?? '';
    this.owningPersonId = configuration?.owningPersonId ?? 0;
    this.syncRequested = false;
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.errorMessageParameters = null;
  }

  // The Core API returns rejections as a ResponseDto, so the specific reason (unrecognised Vectare
  // domain, missing client secret) is in the error body rather than the generic toast.
  private setError(error: unknown): void {
    const body = (error as { error?: { errors?: string[]; messageTranslationParameters?: unknown } })?.error;

    this.errorMessage = body?.errors?.[0] ?? 'SB_Error_Saving';
    this.errorMessageParameters = body?.messageTranslationParameters ?? null;
  }
}
